import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { lectureAPI } from '../lib/api';
import { ImageZoom } from '../components/ImageZoom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    Loader2,
    CheckCircle,
    XCircle,
    ArrowRight,
    ArrowLeft,
    BookOpen,
    GraduationCap,
    Lightbulb,
    Stethoscope,
    ListChecks,
    Table2,
    ImageIcon,
} from 'lucide-react';
import Layout from '../components/Layout';

export default function LearnMode() {
    const { uploadId } = useParams();
    const navigate = useNavigate();
    const { isDark } = useTheme();

    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [lesson, setLesson] = useState(null);
    const [loadingLesson, setLoadingLesson] = useState(false);
    const [diagramLoading, setDiagramLoading] = useState(false);
    const [diagramBase64, setDiagramBase64] = useState(null);

    // Self-check state
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showCheckResult, setShowCheckResult] = useState(false);

    useEffect(() => {
        loadOverview();
    }, [uploadId]);

    const loadOverview = async () => {
        try {
            const res = await lectureAPI.getLearnOverview(uploadId);
            setOverview(res.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load concepts');
            navigate('/study-planner');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (overview) {
            loadLesson(currentIndex);
        }
    }, [currentIndex, overview]);

    const loadLesson = async (idx) => {
        setLesson(null);
        setDiagramBase64(null);
        setDiagramLoading(false);
        setSelectedAnswer(null);
        setShowCheckResult(false);
        setLoadingLesson(true);
        try {
            const res = await lectureAPI.teachConcept(uploadId, idx);
            setLesson(res.data);
            // Start diagram polling if generating
            if (res.data.diagram_status === 'generating' && res.data.id) {
                pollDiagram(res.data.id);
            } else if (res.data.diagram_base64) {
                setDiagramBase64(res.data.diagram_base64);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to generate lesson');
        } finally {
            setLoadingLesson(false);
        }
    };

    const pollDiagram = async (lessonId) => {
        setDiagramLoading(true);
        for (let i = 0; i < 30; i++) {
            await new Promise(r => setTimeout(r, 2000));
            try {
                const res = await lectureAPI.getLessonDiagram(lessonId);
                if (res.data.status === 'ready' && res.data.diagram_base64) {
                    setDiagramBase64(res.data.diagram_base64);
                    setDiagramLoading(false);
                    return;
                }
                if (res.data.status === 'none' || res.data.status === 'failed') {
                    setDiagramLoading(false);
                    return;
                }
            } catch { /* continue */ }
        }
        setDiagramLoading(false);
    };

    const goNext = () => {
        if (overview && currentIndex < overview.total_concepts - 1) {
            setCurrentIndex(prev => prev + 1);
        }
    };

    const goPrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };

    const handleCheckAnswer = (letter) => {
        setSelectedAnswer(letter);
        setShowCheckResult(true);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
            </Layout>
        );
    }

    if (!overview) return null;

    const lessonData = lesson?.lesson;
    const selfCheck = lessonData?.self_check;
    const table = lessonData?.comparison_table;
    const progressPct = ((currentIndex + 1) / overview.total_concepts) * 100;

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-5 animate-fade-in pb-12" data-testid="learn-mode">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                <GraduationCap className="h-3.5 w-3.5 mr-1" />
                                Learn Mode
                            </Badge>
                            <Badge variant="outline">
                                {currentIndex + 1} / {overview.total_concepts}
                            </Badge>
                        </div>
                        <h1 className={cn(
                            "text-xl md:text-2xl font-bold truncate",
                            isDark ? "text-white" : "text-slate-900"
                        )} style={{ fontFamily: 'Manrope' }}>
                            {overview.filename}
                        </h1>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/study-planner')} className="text-slate-500 self-start">
                        Exit
                    </Button>
                </div>

                {/* Progress */}
                <Progress value={progressPct} className="h-2" />

                {/* Concept Selector - scrollable pills */}
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide" data-testid="concept-nav">
                    {overview.concepts.map((c, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentIndex(i)}
                            className={cn(
                                "flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all border",
                                i === currentIndex
                                    ? "bg-emerald-600 text-white border-emerald-600"
                                    : isDark
                                        ? "bg-slate-800 text-slate-300 border-slate-700 hover:border-emerald-500"
                                        : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"
                            )}
                            data-testid={`concept-pill-${i}`}
                        >
                            {c.topic}
                        </button>
                    ))}
                </div>

                {/* Lesson Content */}
                {loadingLesson ? (
                    <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                        <CardContent className="p-10">
                            <div className="flex flex-col items-center gap-3">
                                <div className="relative">
                                    <div className="w-14 h-14 border-4 border-emerald-100 rounded-full" />
                                    <div className="absolute top-0 left-0 w-14 h-14 border-4 border-emerald-500 rounded-full border-t-transparent animate-spin" />
                                </div>
                                <p className={cn("font-medium", isDark ? "text-slate-200" : "text-slate-700")}>
                                    Preparing lesson...
                                </p>
                                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                                    Building a detailed lesson for "{overview.concepts[currentIndex]?.topic}"
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ) : lessonData ? (
                    <div className="space-y-5">
                        {/* Topic Title */}
                        <Card className={cn(
                            "border-l-4 border-l-emerald-500",
                            isDark ? "bg-slate-800 border-slate-700" : ""
                        )}>
                            <CardContent className="p-5">
                                <h2 className={cn(
                                    "text-lg font-bold mb-1",
                                    isDark ? "text-white" : "text-slate-900"
                                )} style={{ fontFamily: 'Manrope' }}>
                                    {lesson.topic}
                                </h2>
                                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                                    Concept {currentIndex + 1} of {overview.total_concepts}
                                    {overview.subject && ` \u00b7 ${overview.subject}`}
                                </p>
                            </CardContent>
                        </Card>

                        {/* Main Explanation */}
                        <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                            <CardHeader className="pb-2">
                                <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "")} style={{ fontFamily: 'Manrope' }}>
                                    <BookOpen className="h-4 w-4 text-emerald-500" />
                                    Explanation
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className={cn("text-sm leading-relaxed whitespace-pre-line", isDark ? "text-slate-200" : "text-slate-700")}>
                                    {lessonData.explanation}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Diagram */}
                        {(diagramBase64 || diagramLoading || lessonData.diagram_description) && (
                            <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                                <CardHeader className="pb-2">
                                    <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "")} style={{ fontFamily: 'Manrope' }}>
                                        <ImageIcon className="h-4 w-4 text-blue-500" />
                                        Diagram
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {diagramBase64 ? (
                                        <div className="rounded-lg overflow-hidden border-2 border-slate-200 bg-white">
                                            <ImageZoom
                                                src={`data:image/png;base64,${diagramBase64}`}
                                                alt="Medical diagram"
                                            />
                                        </div>
                                    ) : diagramLoading ? (
                                        <div className={cn(
                                            "rounded-lg border-2 h-48 flex items-center justify-center",
                                            isDark ? "border-slate-600 bg-slate-700" : "border-slate-200 bg-slate-50"
                                        )}>
                                            <div className="flex flex-col items-center gap-2 text-slate-400">
                                                <Loader2 className="h-6 w-6 animate-spin" />
                                                <span className="text-sm">Generating diagram...</span>
                                            </div>
                                        </div>
                                    ) : lessonData.diagram_description ? (
                                        <p className={cn("text-sm italic", isDark ? "text-slate-400" : "text-slate-500")}>
                                            {lessonData.diagram_description}
                                        </p>
                                    ) : null}
                                </CardContent>
                            </Card>
                        )}

                        {/* Comparison Table */}
                        {table && table.headers && table.rows && (
                            <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                                <CardHeader className="pb-2">
                                    <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "")} style={{ fontFamily: 'Manrope' }}>
                                        <Table2 className="h-4 w-4 text-violet-500" />
                                        {table.title || 'Comparison'}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm border-collapse">
                                            <thead>
                                                <tr>
                                                    {table.headers.map((h, i) => (
                                                        <th key={i} className={cn(
                                                            "text-left px-3 py-2 font-semibold border-b-2",
                                                            isDark ? "border-slate-600 text-slate-200" : "border-slate-300 text-slate-800"
                                                        )}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {table.rows.map((row, ri) => (
                                                    <tr key={ri} className={cn(
                                                        ri % 2 === 0
                                                            ? isDark ? "bg-slate-700/50" : "bg-slate-50"
                                                            : ""
                                                    )}>
                                                        {row.map((cell, ci) => (
                                                            <td key={ci} className={cn(
                                                                "px-3 py-2 border-b",
                                                                ci === 0 && "font-medium",
                                                                isDark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-700"
                                                            )}>{cell}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Clinical Relevance */}
                        {lessonData.clinical_relevance && (
                            <Card className={cn(
                                "border-l-4 border-l-blue-500",
                                isDark ? "bg-slate-800 border-slate-700" : "bg-blue-50/50"
                            )}>
                                <CardHeader className="pb-2">
                                    <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "")} style={{ fontFamily: 'Manrope' }}>
                                        <Stethoscope className="h-4 w-4 text-blue-500" />
                                        Clinical Relevance
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className={cn("text-sm leading-relaxed", isDark ? "text-slate-200" : "text-slate-700")}>
                                        {lessonData.clinical_relevance}
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Key Takeaways */}
                        {lessonData.key_takeaways?.length > 0 && (
                            <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                                <CardHeader className="pb-2">
                                    <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "")} style={{ fontFamily: 'Manrope' }}>
                                        <ListChecks className="h-4 w-4 text-amber-500" />
                                        Key Takeaways
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-2">
                                        {lessonData.key_takeaways.map((t, i) => (
                                            <li key={i} className="flex items-start gap-2.5">
                                                <span className={cn(
                                                    "w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5",
                                                    isDark ? "bg-amber-900/50 text-amber-300" : "bg-amber-100 text-amber-700"
                                                )}>{i + 1}</span>
                                                <span className={cn("text-sm", isDark ? "text-slate-200" : "text-slate-700")}>{t}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        )}

                        {/* Self-Check MCQ */}
                        {selfCheck && (
                            <Card className={cn(
                                "border-2",
                                isDark ? "bg-slate-800 border-emerald-700" : "border-emerald-200 bg-emerald-50/30"
                            )} data-testid="self-check">
                                <CardHeader className="pb-2">
                                    <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "")} style={{ fontFamily: 'Manrope' }}>
                                        <Lightbulb className="h-4 w-4 text-emerald-500" />
                                        Self-Check
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <p className={cn("text-sm font-medium", isDark ? "text-slate-200" : "text-slate-800")}>
                                        {selfCheck.question}
                                    </p>
                                    <div className="space-y-2">
                                        {Object.entries(selfCheck.options || {}).map(([letter, text]) => {
                                            const isSelected = selectedAnswer === letter;
                                            const isCorrect = showCheckResult && letter === selfCheck.correct_answer;
                                            const isWrong = showCheckResult && isSelected && letter !== selfCheck.correct_answer;

                                            return (
                                                <button
                                                    key={letter}
                                                    onClick={() => !showCheckResult && handleCheckAnswer(letter)}
                                                    disabled={showCheckResult}
                                                    className={cn(
                                                        "w-full flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all",
                                                        isDark ? "border-slate-600" : "border-slate-200",
                                                        !showCheckResult && !isDark && "hover:border-emerald-300 hover:bg-emerald-50/50",
                                                        !showCheckResult && isDark && "hover:border-emerald-500 hover:bg-emerald-900/20",
                                                        isCorrect && "border-emerald-500 bg-emerald-100",
                                                        isWrong && "border-red-500 bg-red-100",
                                                        showCheckResult && !isSelected && !isCorrect && "opacity-50"
                                                    )}
                                                    data-testid={`check-option-${letter}`}
                                                >
                                                    <div className={cn(
                                                        "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
                                                        isCorrect && "bg-emerald-600 text-white",
                                                        isWrong && "bg-red-600 text-white",
                                                        !showCheckResult && isDark && "bg-slate-700 text-slate-300",
                                                        !showCheckResult && !isDark && "bg-slate-200 text-slate-600"
                                                    )}>
                                                        {showCheckResult && isCorrect && <CheckCircle className="h-4 w-4" />}
                                                        {showCheckResult && isWrong && <XCircle className="h-4 w-4" />}
                                                        {(!showCheckResult || (!isCorrect && !isWrong)) && letter}
                                                    </div>
                                                    <span className={cn(
                                                        "text-sm flex-1",
                                                        isCorrect && "text-emerald-900 font-medium",
                                                        isWrong && "text-red-900",
                                                        !isCorrect && !isWrong && isDark && "text-slate-200"
                                                    )}>{text}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {showCheckResult && (
                                        <div className={cn(
                                            "p-3 rounded-lg text-sm leading-relaxed mt-2",
                                            selectedAnswer === selfCheck.correct_answer
                                                ? isDark ? "bg-emerald-900/40 text-emerald-200" : "bg-emerald-100 text-emerald-800"
                                                : isDark ? "bg-amber-900/40 text-amber-200" : "bg-amber-100 text-amber-800"
                                        )} data-testid="check-explanation">
                                            {selectedAnswer === selfCheck.correct_answer
                                                ? <span className="font-medium">Correct! </span>
                                                : <span className="font-medium">Not quite. The correct answer is {selfCheck.correct_answer}. </span>
                                            }
                                            {selfCheck.explanation}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ) : null}

                {/* Navigation */}
                <div className="flex items-center justify-between pt-2">
                    <Button
                        variant="outline"
                        onClick={goPrev}
                        disabled={currentIndex === 0 || loadingLesson}
                        data-testid="learn-prev-btn"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Previous
                    </Button>
                    <span className={cn("text-sm font-medium", isDark ? "text-slate-400" : "text-slate-500")}>
                        {currentIndex + 1} of {overview.total_concepts}
                    </span>
                    {currentIndex < overview.total_concepts - 1 ? (
                        <Button
                            onClick={goNext}
                            disabled={loadingLesson}
                            data-testid="learn-next-btn"
                        >
                            Next Concept
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    ) : (
                        <Button
                            onClick={() => navigate('/study-planner')}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="learn-finish-btn"
                        >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Finish
                        </Button>
                    )}
                </div>
            </div>
        </Layout>
    );
}
